<template>
  <div class="container">
    <div class="box-title">
      <h4>
        <i class="fal fa-cogs"></i>
        ລະບົບການຄັດແຍກອັດຕະໂນມັດ
        <span class="countTotal" v-if="machineCode">({{ machineCode }})</span>
      </h4>
    </div>

    <!-- Machine Selection - Compact Bar -->
    <div class="machine-select-bar">
      <div class="machine-select-group">
        <label class="select-label">
          <i class="fal fa-server"></i> ເຄື່ອງ:
        </label>
        <model-list-select
          :list="machineOptions"
          v-model="machineCode"
          placeholder="ເລືອກເຄື່ອງຄັດແຍກ..."
          option-text="display_name"
          option-value="machine_code"
          @input="onMachineChanged"
          class="machine-select"
        />
        <span v-if="server_errors.general" class="text-danger error-text">{{
          server_errors.general
        }}</span>
      </div>
      <div class="machine-status" v-if="machineCode">
        <span :class="['status-badge', hasActiveMode ? 'active' : 'inactive']">
          {{ hasActiveMode ? "ເປີດໃຊ້ງານແລ້ວ" : "ປິດໃຊ້ງານແລ້ວ" }}
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <Loading v-if="isLoading" />

    <!-- Content when machine selected -->
    <template v-if="machineCode && !isLoading">
      <!-- Current Mode Info Card - Compact -->
      <div class="mode-info-bar" v-if="currentMode">
        <div class="info-tags">
          <span class="info-tag mode">
            <i class="fal fa-cog"></i>
            {{ currentMode.mode_display || getModeLabel(currentMode.mode) }}
          </span>
          <span class="info-tag district" v-if="currentMode.district_name">
            <i class="fal fa-map-marker-alt"></i>
            {{ currentMode.district_name }}
          </span>
          <span class="info-tag round" v-if="currentMode.total_rounds > 1">
            <i class="fal fa-sync-alt"></i>
            ຮອບ {{ currentMode.current_round }}/{{ currentMode.total_rounds }}
          </span>
          <span class="info-tag time">
            <i class="fal fa-clock"></i>
            {{ currentMode.activated_at || "-" }}
          </span>
        </div>
        <div class="mode-actions-compact">
          <vs-button
            size="small"
            flat
            @click="refreshMappings()"
            :loading="isRefreshing"
          >
            <i class="fal fa-sync"></i> <span>ອັບເດດຂໍ້ມູນການຄັດແຍກ</span>
          </vs-button>
          <vs-button
            v-if="canSwitchRound"
            size="small"
            flat
            @click="openRoundSelector()"
          >
            <i class="fal fa-exchange"></i> <span>ປ່ຽນຮອບ</span>
          </vs-button>
          <vs-button
            size="small"
            danger
            flat
            @click="confirmDeactivate()"
            :loading="isDeactivating"
          >
            <i class="fal fa-power-off"></i> <span>ປິດໃຊ້ງານ</span>
          </vs-button>
        </div>
      </div>

      <!-- Mode Selection Section -->
      <div class="box">
        <div class="section-header">
          <h5 class="section-title">
            <i class="fal fa-random"></i>
            {{ currentMode ? "ປ່ຽນໂໝດ" : "ຕັ້ງຄ່າໂໝດ" }}
          </h5>
          <vs-button
            :primary="!!currentMode"
            :success="!currentMode"
            @click="onSetMode()"
            :loading="isSettingMode"
            :disabled="selectedMode === 'branch' && !selectedDistrictId"
          >
            <i class="fal fa-check" style="margin-right: 6px"></i>
            {{ currentMode ? "ປ່ຽນໂໝດ" : "ຕັ້ງຄ່າໂໝດ" }}
          </vs-button>
        </div>

        <div class="mode-selection">
          <div
            v-for="option in modeOptions"
            :key="option.value"
            :class="[
              'mode-option',
              { selected: selectedMode === option.value },
            ]"
            @click="selectedMode = option.value"
          >
            <div class="mode-icon">
              <i :class="getModeIcon(option.value)"></i>
            </div>
            <div class="mode-info">
              <span class="mode-label">{{ option.label }}</span>
              <span class="mode-desc">{{ option.description }}</span>
            </div>
            <div class="mode-check" v-if="selectedMode === option.value">
              <i class="fal fa-check-circle"></i>
            </div>
          </div>
        </div>

        <!-- District selection for branch mode -->
        <div v-if="selectedMode === 'branch'" class="district-selection">
          <div class="district-row">
            <div class="district-select-group">
              <label class="label">
                ເລືອກເມືອງ
                <span class="text-danger">* {{ server_errors.district }}</span>
              </label>
              <model-list-select
                :list="districts"
                v-model="selectedDistrictId"
                placeholder="ເລືອກເມືອງ..."
                option-text="name"
                option-value="id"
                @input="onDistrictChange"
              >
                <template v-slot:option="{ option }">
                  <span>{{ option.name }}</span>
                  <span class="branch-count"
                    >({{ option.branch_count }} ສາຂາ,
                    {{ option.rounds_needed }} ຮອບ)</span
                  >
                </template>
              </model-list-select>
            </div>

            <!-- Round info when district selected -->
            <div
              v-if="selectedDistrictId && rounds.length > 0"
              class="round-info"
            >
              <label class="label">ເລືອກຮອບ</label>
              <div class="rounds-grid">
                <div
                  v-for="round in rounds"
                  :key="round.round"
                  :class="[
                    'round-item',
                    { selected: currentRound === round.round },
                  ]"
                  @click="currentRound = round.round"
                >
                  <span class="round-number">ຮອບ {{ round.round }}</span>
                  <span class="round-detail"
                    >{{ round.branch_count }} ສາຂາ ({{
                      round.branch_range
                    }})</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Dashboard Section -->
      <div class="box dashboard-section">
        <div class="section-header">
          <h5 class="section-title">
            <i class="fal fa-tachometer-alt"></i>
            Dashboard ສະຖານະເຄື່ອງຄັດແຍກ
          </h5>
          <div class="refresh-controls">
            <span
              class="refresh-status"
              :class="{ active: autoRefreshEnabled }"
            >
              <i
                class="fal fa-sync-alt"
                :class="{ 'fa-spin': autoRefreshEnabled }"
              ></i>
              {{ autoRefreshEnabled ? "ອັບເດດອັດຕະໂນມັດ" : "ຢຸດອັບເດດ" }}
            </span>
            <select
              class="interval-select"
              :value="refreshIntervalMs"
              @change="changeRefreshInterval(Number($event.target.value))"
            >
              <option
                v-for="opt in refreshIntervalOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <vs-button
              :warn="autoRefreshEnabled"
              :success="!autoRefreshEnabled"
              size="small"
              @click="toggleAutoRefresh()"
            >
              <i
                :class="autoRefreshEnabled ? 'fal fa-pause' : 'fal fa-play'"
                style="margin-right: 6px"
              ></i>
              {{ autoRefreshEnabled ? "ຢຸດ" : "ເລີ່ມ" }}
            </vs-button>
            <vs-button
              flat
              size="small"
              @click="fetchDashboard()"
              :loading="isDashboardLoading"
            >
              <i class="fal fa-sync" style="margin-right: 6px"></i>
              ໂຫຼດໃໝ່
            </vs-button>
          </div>
        </div>

        <!-- Today Stats Cards -->
        <div class="stats-section" v-if="todayStats">
          <div class="stats-header">
            <h5 class="stats-title">
              <i class="fal fa-chart-bar"></i> ສະຖິຕິປະຈຳວັນ
              <span v-if="!selectedStatsDate"
                >- {{ currentDateFormatted }}</span
              >
            </h5>
            <div class="stats-date-filter">
              <label class="date-filter-label">
                <i class="fal fa-calendar-alt"></i> ວັນທີ:
              </label>
              <date-picker
                v-model="selectedStatsDate"
                type="date"
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                placeholder="ເລືອກວັນທີ"
                :clearable="true"
                :disabled-date="(date) => date > new Date()"
                @change="onStatsDateChange"
                class="stats-date-picker"
              />
            </div>
          </div>
          <div class="stats-cards">
            <div
              class="stat-card clickable"
              @click="
                openSortedPackages(
                  'sorted,phase_1_completed,phase_2_completed,route_completed'
                )
              "
              title="ຄລິກເພື່ອເບິ່ງລາຍລະອຽດ"
            >
              <div class="stat-icon packages">
                <i class="fal fa-box"></i>
              </div>
              <div class="stat-info">
                <span class="stat-value">{{ todayStats.packages_sorted }}</span>
                <span class="stat-label">ຄັດແຍກແລ້ວ</span>
              </div>
              <i class="fal fa-external-link stat-link-icon"></i>
            </div>
            <div
              class="stat-card clickable"
              @click="openSortedPackages('packed')"
              title="ຄລິກເພື່ອເບິ່ງລາຍລະອຽດ"
            >
              <div class="stat-icon packed">
                <i class="fal fa-boxes"></i>
              </div>
              <div class="stat-info">
                <span class="stat-value">{{ todayStats.packages_packed }}</span>
                <span class="stat-label">ແພັກແລ້ວ</span>
              </div>
              <i class="fal fa-external-link stat-link-icon"></i>
            </div>
            <div
              class="stat-card clickable"
              @click="openPendingPackages('pending')"
              title="ຄລິກເພື່ອເບິ່ງລາຍລະອຽດ"
            >
              <div class="stat-icon pending">
                <i class="fal fa-clock"></i>
              </div>
              <div class="stat-info">
                <span class="stat-value">{{
                  todayStats.packages_pending
                }}</span>
                <span class="stat-label">ລໍຖ້າ</span>
              </div>
              <i class="fal fa-external-link stat-link-icon"></i>
            </div>
            <div
              class="stat-card clickable"
              @click="openPendingPackages('sorting')"
              title="ຄລິກເພື່ອເບິ່ງລາຍລະອຽດ"
            >
              <div class="stat-icon sorting">
                <i class="fal fa-check-circle"></i>
              </div>
              <div class="stat-info">
                <span class="stat-value">{{
                  todayStats.packages_sorting
                }}</span>
                <span class="stat-label">ລໍຖ້າຢືນຢັນ</span>
              </div>
              <i class="fal fa-external-link stat-link-icon"></i>
            </div>
            <div class="stat-card">
              <div class="stat-icon containers">
                <i class="fal fa-pallet"></i>
              </div>
              <div class="stat-info">
                <span class="stat-value">{{
                  todayStats.containers_created
                }}</span>
                <span class="stat-label">ຖົງສ້າງແລ້ວ</span>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon weight">
                <i class="fal fa-weight"></i>
              </div>
              <div class="stat-info">
                <span class="stat-value"
                  >{{ todayStats.total_weight_kg }} kg</span
                >
                <span class="stat-label">ນ້ຳໜັກລວມ</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Chutes Summary -->
        <div class="chutes-summary" v-if="chutesSummary">
          <h6 class="summary-title">ສະຖານະຊ່ອງຮັບພັດສະດຸທັງໝົດ</h6>
          <div class="summary-badges">
            <span class="summary-badge total">
              <i class="fal fa-layer-group"></i>
              ທັງໝົດ: {{ chutesSummary.total }}
            </span>
            <span class="summary-badge empty">
              <i class="fal fa-inbox"></i>
              ວ່າງ: {{ chutesSummary.empty }}
            </span>
            <span class="summary-badge active">
              <i class="fal fa-play-circle"></i>
              ກຳລັງໃຊ້ງານ: {{ chutesSummary.active }}
            </span>
            <span class="summary-badge full">
              <i class="fal fa-check-circle"></i>
              ເຕັມແລ້ວ: {{ chutesSummary.full }}
            </span>
            <!--            <span class="summary-badge packing">-->
            <!--              <i class="fal fa-box-open"></i>-->
            <!--              ກຳລັງແພັກ: {{ chutesSummary.packing }}-->
            <!--            </span>-->
            <span class="summary-badge error">
              <i class="fal fa-exclamation-triangle"></i>
              ຊ່ອງຂໍ້ຜິດພາດ: {{ chutesSummary.error }} ຊ່ອງ
            </span>
          </div>
        </div>

        <!-- Alerts -->
        <div
          class="alerts-section"
          v-if="dashboardAlerts && dashboardAlerts.length > 0"
        >
          <h6 class="alerts-title">
            <i class="fal fa-bell"></i>
            ການແຈ້ງເຕືອນ
          </h6>
          <div class="alerts-list">
            <div
              v-for="(alert, index) in dashboardAlerts"
              :key="index"
              :class="['alert-item', getAlertSeverityClass(alert.severity)]"
            >
              <i
                :class="
                  alert.severity === 'error'
                    ? 'fal fa-exclamation-circle'
                    : 'fal fa-info-circle'
                "
              ></i>
              <span class="alert-message">{{ alert.message }}</span>
              <span class="alert-time">{{ alert.timestamp }}</span>
            </div>
          </div>
        </div>

        <!-- Chutes Grid - Uses mergedChutes computed for optimized rendering -->
        <div class="chutes-grid" v-if="mergedChutes.length > 0">
          <h6 class="grid-title">ລາຍລະອຽດຊ່ອງຮັບພັດສະດຸ</h6>
          <div class="chutes-container">
            <div
              v-for="chute in mergedChutes"
              :key="chute.chute_number"
              :class="[
                'chute-card',
                getChuteStatusClass(chute.status),
                {
                  'error-chute': chute.is_error_chute,
                  'machine-dc': chute.is_machine_dc,
                },
              ]"
            >
              <div class="chute-header">
                <span class="chute-num"
                  >ຊ່ອງ {{ String(chute.chute_number).padStart(2, "0") }}</span
                >
                <span class="chute-status-badge">{{
                  getChuteStatusLabel(chute.status)
                }}</span>
              </div>
              <div class="chute-body">
                <div
                  class="chute-destination"
                  v-if="chute.destination_name"
                  :class="{ 'is-dc': chute.is_machine_dc }"
                >
                  <i
                    :class="
                      chute.is_machine_dc
                        ? 'fal fa-warehouse'
                        : 'fal fa-map-marker-alt'
                    "
                  ></i>
                  {{ chute.destination_name }}
                </div>
                <div
                  class="chute-destination error"
                  v-else-if="chute.is_error_chute"
                >
                  <i class="fal fa-exclamation-triangle"></i>
                  ຊ່ອງຮັບເຄື່ອງມີປັນຫາ
                </div>
                <div class="chute-destination empty" v-else>
                  <i class="fal fa-minus-circle"></i>
                  ບໍ່ມີການກຳນົດ
                </div>
                <div class="chute-stats">
                  <span class="chute-count">
                    <i class="fal fa-box"></i>
                    {{ chute.current_package_count || 0 }}/{{
                      chute.capacity_packages || 0
                    }}
                  </span>
                  <span class="chute-weight">
                    <i class="fal fa-weight"></i>
                    {{ chute.total_weight_kg || 0 }}kg
                  </span>
                </div>
                <div class="chute-progress">
                  <div
                    class="progress-bar"
                    :style="{ width: (chute.utilization_percent || 0) + '%' }"
                  ></div>
                </div>
                <span class="utilization-text"
                  >{{ chute.utilization_percent || 0 }}%</span
                >
              </div>
              <!-- Print button for non-empty, non-error chutes -->
              <div
                class="chute-footer"
                v-if="
                  chute.status !== 'empty' &&
                  chute.status !== 'error' &&
                  !chute.is_error_chute
                "
              >
                <vs-button
                  success
                  size="small"
                  flat
                  @click="printBoxBarcode(chute.chute_number)"
                  :loading="printingChuteNumber === chute.chute_number"
                >
                  <i class="fal fa-print"></i>
                </vs-button>
              </div>
              <!-- Clear button for error chutes or is_error_chute with non-empty status -->
              <div
                class="chute-footer"
                v-else-if="
                  chute.status === 'error' ||
                  (chute.is_error_chute && chute.status !== 'empty')
                "
              >
                <vs-button
                  danger
                  size="small"
                  flat
                  @click="clearErrorChute(chute.chute_number)"
                  :loading="clearingChuteNumber === chute.chute_number"
                >
                  <i class="fal fa-broom"></i> ລ້າງ
                </vs-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Round Switch Dialog -->
    <ModalEdit>
      <template v-slot="{ close }">
        <RoundSelectorDialog
          :rounds="rounds"
          :currentRound="currentRound"
          :machineCode="machineCode"
          @close="close"
          @success="onRoundSwitched"
        />
      </template>
    </ModalEdit>

    <!-- Deactivate Confirmation Dialog -->
    <ModalDelete>
      <template v-slot="{ close }">
        <DeactivateConfirmDialog
          :machineCode="machineCode"
          @close="close"
          @confirm="onDeactivateConfirmed"
        />
      </template>
    </ModalDelete>

    <!-- Active Chutes Error Dialog -->
    <vs-dialog v-model="showActiveChutesDialog" width="500px">
      <ActiveChutesErrorDialog
        v-if="activeChutesError"
        :chutes="activeChutesError.chutes"
        :message="activeChutesError.message"
        :printingChuteNumber="printingChuteNumber"
        :clearingChuteNumber="clearingChuteNumber"
        @close="closeActiveChutesDialog"
        @clear-chute="clearActiveChute"
        @print-chute="printActiveChute"
      />
    </vs-dialog>

    <!-- Pending Packages Error Dialog -->
    <vs-dialog v-model="showPendingPackagesDialog" width="500px">
      <PendingPackagesErrorDialog
        v-if="pendingPackagesError"
        :packages="pendingPackagesError.packages"
        :message="pendingPackagesError.message"
        :chuteNumber="pendingPackagesError.chuteNumber"
        :machineCode="machineCode"
        @close="closePendingPackagesDialog"
        @skip-success="handleSkipPendingPackagesSuccess"
      />
    </vs-dialog>
  </div>
</template>

<script>
import SortingModeManagementMixin from "@/Mixins/Distribution-Center/SortingMode/SortingModeManagement";
import { ModelListSelect } from "vue-search-select";
import Loading from "@/views/Loading";
import RoundSelectorDialog from "./Dialog/RoundSelectorDialog";
import DeactivateConfirmDialog from "./Dialog/DeactivateConfirmDialog";
import ActiveChutesErrorDialog from "./Dialog/ActiveChutesErrorDialog";
import PendingPackagesErrorDialog from "./Dialog/PendingPackagesErrorDialog";
import DatePicker from "vue2-datepicker";

export default {
  name: "SortingModeManagement",

  components: {
    ModelListSelect,
    Loading,
    RoundSelectorDialog,
    DeactivateConfirmDialog,
    ActiveChutesErrorDialog,
    PendingPackagesErrorDialog,
    DatePicker,
  },

  mixins: [SortingModeManagementMixin],

  computed: {
    machineOptions() {
      return this.machines.map((machine) => ({
        ...machine,
        display_name: `${machine.machine_code} - ${machine.machine_name} - ${machine.distribution_center_name}`,
      }));
    },
    currentDateFormatted() {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, "0");
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const year = now.getFullYear();
      return `${day}/${month}/${year}`;
    },
  },

  created() {
    this.fetchMachines();
  },

  methods: {
    getModeIcon(mode) {
      const icons = {
        province_dc: "fal fa-globe-asia",
        district: "fal fa-map-marked-alt",
        branch: "fal fa-store-alt",
      };
      return icons[mode] || "fal fa-cog";
    },

    onMachineChanged() {
      if (this.machineCode) {
        this.loadMachineData();
      }
    },

    onDistrictChange() {
      if (this.selectedDistrictId) {
        this.getRounds(this.selectedDistrictId);
      }
    },

    async onSetMode() {
      const success = await this.setMode();
      if (success) {
        await this.getCurrentMode();
      }
    },

    openRoundSelector() {
      this.$store.commit("modalEdit_State", true);
    },

    confirmDeactivate() {
      this.$store.commit("modalDelete_State", true);
    },

    async onRoundSwitched(round) {
      await this.switchRound(round);
      this.$store.commit("modalEdit_State", false);
    },

    async onDeactivateConfirmed() {
      await this.deactivateMode();
      this.$store.commit("modalDelete_State", false);
    },
  },
};
</script>

<style lang="scss" scoped>
// Compact spacing for better vertical view
.container {
  .box {
    margin-bottom: 10px;
    padding: 12px 15px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .box-title {
    margin-bottom: 10px;

    h4 {
      margin-bottom: 0;
      font-size: 18px;
    }
  }
}

// Compact Machine Selection Bar
.machine-select-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 15px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.machine-select-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;

  .select-label {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;

    i {
      color: #195bff;
      margin-right: 4px;
    }
  }

  .machine-select,
  ::v-deep .ui.search.dropdown {
    flex: 1;
    width: 100% !important;
  }

  .error-text {
    font-size: 11px;
  }
}

.machine-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 15px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 11px;
  font-weight: 600;

  &.active {
    background: #e8f5e9;
    color: #2e7d32;
  }

  &.inactive {
    background: #ffebee;
    color: #c62828;
  }
}

// Compact Mode Info Bar
.mode-info-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px;
  background: white;
  border: 1px solid #e0e0e0;
  border-left: 3px solid #195bff;
  border-radius: 6px;
  margin-bottom: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.info-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.info-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;

  i {
    font-size: 11px;
  }

  &.mode {
    background: #195bff;
    color: white;
  }

  &.district {
    background: #e8f5e9;
    color: #2e7d32;
  }

  &.round {
    background: #fff3e0;
    color: #ef6c00;
  }

  &.time {
    background: #f5f5f5;
    color: #666;
  }
}

.mode-actions-compact {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;

  .vs-button {
    i {
      margin-right: 4px;
    }

    span {
      font-size: 11px;
    }
  }

  @media (max-width: 576px) {
    width: 100%;
    justify-content: flex-start;

    .vs-button {
      flex: 1;
      min-width: 0;
    }
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  flex-wrap: wrap;
  gap: 10px;

  .section-title {
    margin-bottom: 0;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #195bff;
  margin-bottom: 12px;

  i {
    margin-right: 6px;
  }
}

.mode-selection {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.mode-option {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #195bff;
    background: #f5f8ff;
  }

  &.selected {
    border-color: #195bff;
    background: #f5f8ff;
  }

  .mode-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0f0f0;
    border-radius: 8px;
    margin-right: 10px;

    i {
      font-size: 20px;
      color: #195bff;
    }
  }

  .mode-info {
    flex: 1;

    .mode-label {
      display: block;
      font-weight: 600;
      color: #333;
      margin-bottom: 2px;
      font-size: 13px;
    }

    .mode-desc {
      display: block;
      font-size: 11px;
      color: #666;
    }
  }

  .mode-check {
    i {
      font-size: 20px;
      color: #195bff;
    }
  }
}

.district-selection {
  margin-top: 10px;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 8px;

  .label {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
    display: block;
  }

  .branch-count {
    font-size: 10px;
    color: #666;
    margin-left: 6px;
  }

  .district-row {
    display: flex;
    align-items: flex-start;
    gap: 20px;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 15px;
    }
  }

  .district-select-group {
    flex-shrink: 0;
    width: 220px;

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  // District model-list-select
  ::v-deep .ui.search.dropdown {
    min-height: 40px !important;
    width: 100% !important;
    font-size: 14px !important;

    > .text {
      font-size: 14px !important;
    }

    input.search {
      padding: 8px 12px !important;
      font-size: 14px !important;
    }

    .menu {
      font-size: 14px !important;
      min-width: 220px !important;

      > .item {
        padding: 10px 12px !important;
        font-size: 14px !important;
      }
    }
  }
}

.round-info {
  flex: 1;

  .label {
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    padding-left: 8px;
    margin-bottom: 6px;
    display: block;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
}

.rounds-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.round-item {
  padding: 2px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;
  background: white;
  min-width: 100px;

  &:hover {
    border-color: #195bff;
    background: #f5f8ff;
  }

  &.selected {
    border-color: #195bff;
    background: #195bff;
    color: white;

    .round-number,
    .round-detail {
      color: white;
    }
  }

  .round-number {
    display: block;
    font-weight: 600;
    font-size: 12px;
    color: #333;
  }

  .round-detail {
    display: block;
    font-size: 10px;
    color: #666;
    margin-top: 2px;
  }
}

.chute-number {
  display: inline-block;
  width: 30px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  background: #195bff;
  color: white;
  border-radius: 5px;
  font-weight: 600;

  &.error {
    background: #c62828;
  }
}

.machine-dc-row {
  background: #e3f2fd !important;
}

.error-chute-row {
  background: #ffebee !important;
}

// Dashboard Styles
.dashboard-section {
  margin-top: 0;
}

.refresh-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;

  // Fix vuesax button styles in this container
  ::v-deep .vs-button {
    flex-shrink: 0 !important;
    min-width: auto !important;
  }

  @media (max-width: 576px) {
    width: 100%;
    justify-content: flex-start;
    gap: 6px;

    .refresh-status {
      order: 3;
      width: 100%;
      justify-content: center;
      margin-top: 4px;
    }
  }
}

.refresh-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #666;
  padding: 4px 10px;
  background: #f5f5f5;
  border-radius: 12px;
  white-space: nowrap;
  flex-shrink: 0;

  &.active {
    color: #2e7d32;
    background: #e8f5e9;
  }

  i {
    font-size: 12px;
  }
}

.refresh-controls .interval-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding: 5px 24px 5px 10px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #ddd;
  border-radius: 6px;
  background-color: white;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 6px center;
  color: #333;
  cursor: pointer;
  outline: none;
  width: 55px;
  max-width: 55px;
  min-width: 55px;
  height: 28px;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    border-color: #195bff;
  }

  &:focus {
    border-color: #195bff;
    box-shadow: 0 0 0 2px rgba(25, 91, 255, 0.1);
  }
}

.stats-section {
  margin-bottom: 12px;
}

.stats-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;

  .stats-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #333;

    i {
      margin-right: 6px;
      color: #667eea;
    }
  }

  .stats-date-filter {
    display: flex;
    align-items: center;
    gap: 8px;

    .date-filter-label {
      font-size: 12px;
      color: #666;
      font-weight: 500;
      white-space: nowrap;

      i {
        margin-right: 4px;
        color: #195bff;
      }
    }

    .stats-date-picker {
      width: 150px;

      ::v-deep .mx-input {
        height: 32px;
        font-size: 12px;
        border-radius: 6px;
        border: 1px solid #e0e0e0;
        font-family: "Open Sans", "BoonBaan", "Noto Sans Lao", sans-serif;

        &:hover,
        &:focus {
          border-color: #195bff;
        }
      }

      ::v-deep .mx-icon-calendar {
        color: #195bff;
      }
    }
  }

  .stats-date {
    font-size: 13px;
    color: #666;
    font-weight: 500;

    i {
      margin-right: 5px;
      color: #195bff;
    }
  }
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 10px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  border: 1px solid #eee;

  .stat-icon {
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    margin-right: 10px;

    i {
      font-size: 18px;
      color: white;
    }

    &.packages {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }
    &.packed {
      background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
    }
    &.pending {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    }
    &.sorting {
      background: linear-gradient(135deg, #f2994a 0%, #f2c94c 100%);
    }
    &.containers {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    }
    &.weight {
      background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
    }
  }

  .stat-info {
    .stat-value {
      display: block;
      font-size: 18px;
      font-weight: 700;
      color: #333;
    }

    .stat-label {
      display: block;
      font-size: 11px;
      color: #666;
    }
  }

  &.clickable {
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      border-color: #f5576c;

      .stat-link-icon {
        opacity: 1;
        color: #f5576c;
      }
    }

    .stat-link-icon {
      position: absolute;
      top: 8px;
      right: 8px;
      font-size: 12px;
      color: #999;
      opacity: 0.5;
      transition: all 0.2s ease;
    }
  }
}

.chutes-summary {
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 8px;

  .summary-title {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  .summary-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .summary-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 15px;
    font-size: 12px;
    font-weight: 500;

    i {
      font-size: 12px;
    }

    &.total {
      background: #e3f2fd;
      color: #1565c0;
    }
    &.empty {
      background: #f5f5f5;
      color: #616161;
    }
    &.active {
      background: #e8f5e9;
      color: #2e7d32;
    }
    &.full {
      background: #fff3e0;
      color: #ef6c00;
    }
    &.packing {
      background: #e1f5fe;
      color: #0277bd;
    }
    &.error {
      background: #ffebee;
      color: #c62828;
    }
  }
}

.alerts-section {
  margin-bottom: 12px;

  .alerts-title {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;

    i {
      margin-right: 5px;
      color: #ff9800;
    }
  }

  .alerts-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .alert-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;

    i {
      font-size: 14px;
    }

    .alert-message {
      flex: 1;
    }

    .alert-time {
      font-size: 10px;
      color: #999;
    }

    &.alert-info {
      background: #e3f2fd;
      color: #1565c0;
    }

    &.alert-warning {
      background: #fff3e0;
      color: #ef6c00;
    }

    &.alert-error {
      background: #ffebee;
      color: #c62828;
    }

    &.alert-critical {
      background: #c62828;
      color: white;
    }
  }
}

.chutes-grid {
  .grid-title {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
  }

  .chutes-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
  }
}

.chute-card {
  background: white;
  border-radius: 8px;
  border: 2px solid #bdc3c7;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  }

  .chute-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 10px;
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
  }

  .chute-num {
    font-size: 15px;
    font-weight: 700;
    color: #333;
  }

  .chute-status-badge {
    font-size: 9px;
    padding: 2px 6px;
    border-radius: 8px;
    font-weight: 600;
  }

  .chute-body {
    padding: 8px 10px;
  }

  .chute-destination {
    font-size: 11px;
    color: #333;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 4px;

    i {
      color: #195bff;
      font-size: 11px;
    }

    &.empty {
      color: #999;
      i {
        color: #999;
      }
    }

    &.is-dc {
      color: #1565c0;
      font-weight: 600;
      i {
        color: #1565c0;
      }
    }

    &.error {
      color: #c62828;
      i {
        color: #c62828;
      }
    }
  }

  // Machine DC chute highlight
  &.machine-dc {
    border: 2px solid #1565c0;
    .chute-header {
      background: #e3f2fd;
    }
  }

  .chute-stats {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #666;
    margin-bottom: 6px;

    i {
      margin-right: 3px;
    }
  }

  .chute-progress {
    height: 4px;
    background: #e0e0e0;
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 3px;

    .progress-bar {
      height: 100%;
      background: linear-gradient(90deg, #4caf50 0%, #8bc34a 100%);
      border-radius: 2px;
      transition: width 0.3s ease;
    }
  }

  .utilization-text {
    font-size: 9px;
    color: #999;
  }

  .chute-footer {
    display: flex;
    justify-content: flex-end;
    padding: 6px 10px;
    border-top: 1px solid #e0e0e0;
    background: #fafafa;
  }

  // Status styles
  &.chute-empty {
    border: 2px solid #636e72;
    .chute-header {
      background: linear-gradient(135deg, #636e72 0%, #4a5568 100%);
    }
    .chute-num {
      color: white;
    }
    .chute-status-badge {
      background: #e0e0e0;
      color: #666;
    }
  }

  &.chute-active {
    border: 2px solid #4caf50;
    .chute-header {
      background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
    }
    .chute-num {
      color: white;
    }
    .chute-status-badge {
      background: #4caf50;
      color: white;
    }
    .progress-bar {
      background: linear-gradient(90deg, #4caf50 0%, #8bc34a 100%);
    }
  }

  &.chute-full {
    border: 2px solid #ff9800;
    .chute-header {
      background: linear-gradient(135deg, #f7971e 0%, #ffd200 100%);
    }
    .chute-num {
      color: white;
    }
    .chute-status-badge {
      background: #ff9800;
      color: white;
    }
    .progress-bar {
      background: linear-gradient(90deg, #ff9800 0%, #ffc107 100%);
    }
  }

  &.chute-packing {
    border: 2px solid #2196f3;
    .chute-header {
      background: linear-gradient(135deg, #1e88e5 0%, #42a5f5 100%);
    }
    .chute-num {
      color: white;
    }
    .chute-status-badge {
      background: #2196f3;
      color: white;
    }
    .progress-bar {
      background: linear-gradient(90deg, #2196f3 0%, #03a9f4 100%);
    }
  }

  // Status error - warning color (orange)
  &.chute-error {
    border: 2px solid #ff9800;
    .chute-header {
      background: linear-gradient(135deg, #f57c00 0%, #ff9800 100%);
    }
    .chute-num {
      color: white;
    }
    .chute-status-badge {
      background: #ff9800;
      color: white;
    }
    .progress-bar {
      background: linear-gradient(90deg, #ff9800 0%, #ffc107 100%);
    }
  }

  // Error/Overflow chute - danger color (red)
  &.error-chute {
    border: 2px solid #f44336;
    .chute-header {
      background: linear-gradient(135deg, #c62828 0%, #e53935 100%);
    }
    .chute-num {
      color: white;
    }
    .chute-status-badge {
      background: #f44336;
      color: white;
    }
    .progress-bar {
      background: linear-gradient(90deg, #f44336 0%, #e91e63 100%);
    }
  }
}
</style>
